import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['list', 'emptyState', 'submitButton']

  static DAY_OF_WEEK_OPTIONS = [
    { value: 1, label: 'Segunda' },
    { value: 2, label: 'Terça' },
    { value: 3, label: 'Quarta' },
    { value: 4, label: 'Quinta' },
    { value: 5, label: 'Sexta' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ]

  static FIELD_METADATA = {
    'current_day_of_week_number': { type: 'day_of_week', label: '📅 Dia da semana', group: 'Tempo' },
    'transaction_type': {
      type: 'select', label: 'Tipo de transação', group: 'Lead',
      options: [
        { value: 'rent', label: 'Locação' },
        { value: 'sale', label: 'Venda' }
      ]
    },
    'usage_type': {
      type: 'select', label: 'Tipo de uso', group: 'Lead',
      options: [
        { value: 'residential', label: 'Residencial' },
        { value: 'commercial', label: 'Comercial' }
      ]
    },
    'origin_domain': { type: 'text', label: 'Domínio de origem', group: 'Lead' },
    'offer_type': {
      type: 'offer_type', label: 'Natureza do contato', group: 'Lead',
      options: [
        { value: 'broker_partnership', label: 'Corretor propondo parceria' },
        { value: 'service_provider', label: 'Prestador de serviço' },
        { value: 'job_seeker', label: 'Candidato a vaga de emprego' },
        { value: 'listing_removal', label: 'Pedido de retirada de anúncio' }
      ]
    },
    'campaign_name': { type: 'text', label: 'Campanha Meta', group: 'Lead' },
    'source_identifier': { type: 'text', label: 'Identificador de origem', group: 'Lead' },
    'property_acquisition?': { type: 'boolean', label: 'É captação', group: 'Lead' },
    'unknown_answer': { type: 'boolean', label: 'Qualificação por pergunta desconhecida', group: 'Lead' },
    'manual_qualification': { type: 'boolean', label: 'Qualificação manual', group: 'Lead' },
    'forced_qualification': { type: 'boolean', label: 'Qualificação forçada', group: 'Lead' },
    'high_priority': { type: 'boolean', label: 'Alta prioridade', group: 'Lead' },
    'human_requested?': { type: 'boolean', label: 'Humano solicitado', group: 'Lead' },
    'conversation.conversation_user.name': { type: 'text', label: 'Nome do contato', group: 'Conversa' },
    'conversation.conversation_user.phone': { type: 'text', label: 'Telefone do contato', group: 'Conversa' },
    'conversation.conversation_user.email': { type: 'text', label: 'Email do contato', group: 'Conversa' },
    'agent_phone': { type: 'text', label: 'Número do agente (WhatsApp)', group: 'Conversa' },
    'conversation.user_replied?': { type: 'boolean', label: 'Usuário respondeu', group: 'Conversa' },
    'conversation.engaged_by_pipeline?': { type: 'boolean', label: 'Participou de engajamento automático', group: 'Conversa' },
    'user_messages_count': { type: 'number', label: 'Nº de mensagens do usuário', group: 'Conversa' },
    'development': { type: 'boolean', label: 'Conversa sobre lançamentos', group: 'Conversa' },
    'listing.development': { type: 'boolean', label: 'É lançamento', group: 'Imóvel' },
    'listing.list_price': { type: 'number', label: 'Preço de venda', group: 'Imóvel' },
    'listing.rental_price': { type: 'number', label: 'Preço de aluguel', group: 'Imóvel' },
    'listing.location.area': { type: 'text', label: 'Zona/Área', group: 'Imóvel' },
    'listing.location.city': { type: 'text', label: 'Cidade', group: 'Imóvel' },
    'listing.crm_id': { type: 'text', label: 'CRM ID do imóvel', group: 'Imóvel' },
    'listing.crm_data.agency_slug': { type: 'text', label: 'Agência (filial)', group: 'Imóvel' }
  }

  connect() {
    this.conditionIndex = this.getNextConditionIndex()
  }

  getNextConditionIndex() {
    const items = this.listTarget.querySelectorAll('.condition-item')
    let maxIndex = -1

    items.forEach(item => {
      const fieldSelect = item.querySelector('select[name$="[field]"]')
      if (fieldSelect) {
        const match = fieldSelect.name.match(/\[(\d+)\]/)
        if (match) {
          const idx = parseInt(match[1], 10)
          if (idx > maxIndex) maxIndex = idx
        }
      }
    })

    return maxIndex + 1
  }

  addCondition(event) {
    event?.preventDefault()

    const index = this.conditionIndex
    this.conditionIndex++

    if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()

    const html = this.buildConditionElement(index)
    this.listTarget.insertAdjacentHTML('beforeend', html)

    this.updateSubmitButton()
  }

  removeCondition(event) {
    event?.preventDefault()
    const item = event.currentTarget.closest('.condition-item')
    if (!item) return

    item.remove()

    if (this.listTarget.querySelectorAll('.condition-item').length === 0) {
      this.listTarget.insertAdjacentHTML('beforeend', this.buildEmptyState())
    }

    this.updateSubmitButton()
  }

  updateField(event) {
    const field = event.target.value
    const item = event.target.closest('.condition-item')
    if (!item) return

    const meta = this.constructor.FIELD_METADATA[field] || { type: 'text' }
    const operatorSelect = item.querySelector('select[name$="[operator]"]')
    const valueContainer = item.querySelector('[data-value-container]')

    if (operatorSelect) {
      operatorSelect.innerHTML = this.buildOperatorOptions(meta.type)
      operatorSelect.selectedIndex = 0
      if (meta.type === 'boolean') operatorSelect.value = 'equals'
    }

    if (valueContainer) {
      const operator = operatorSelect ? operatorSelect.value : ''
      this.rebuildValueInput(valueContainer, field, null, operator)
    }
  }

  updateOperator(event) {
    const operator = event.target.value
    const item = event.target.closest('.condition-item')
    if (!item) return

    const fieldSelect = item.querySelector('select[name$="[field]"]')
    const valueContainer = item.querySelector('[data-value-container]')

    if (valueContainer && fieldSelect) {
      const field = fieldSelect.value
      // 'in' and 'not_in' share the same day picker — rebuilding would wipe the selection
      if ((this.constructor.FIELD_METADATA[field] || {}).type === 'day_of_week') return

      const currentValue = this.getCurrentValue(valueContainer)
      this.rebuildValueInput(valueContainer, field, currentValue, operator)
    }
  }

  dayCheckboxChanged(event) {
    const container = event.target.closest('[data-value-container]')
    if (!container) return

    const hidden = container.querySelector('input[type="hidden"][name$="[value]"]')
    const selected = Array.from(container.querySelectorAll('input[type="checkbox"]:checked'))
      .map(cb => Number(cb.value))
    if (hidden) hidden.value = JSON.stringify(selected)

    const label = event.target.closest('label')
    if (label) label.className = this.dayLabelClass(event.target.checked)
  }

  // --- Private helpers ---

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const hasConditions = this.listTarget.querySelectorAll('.condition-item').length > 0

    if (hasConditions) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('btn-disabled', 'opacity-50')
      this.submitButtonTarget.title = ''
    } else {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('btn-disabled', 'opacity-50')
      this.submitButtonTarget.title = 'Adicione pelo menos uma condição para continuar'
    }
  }

  getCurrentValue(container) {
    const checked = container.querySelector('input[type="radio"]:checked')
    if (checked) return checked.value

    const input = container.querySelector('input, select')
    return input ? input.value : null
  }

  buildConditionElement(index) {
    const prefix = `conditions[${index}]`

    return `
      <div class="condition-item bg-gray-50 border border-gray-200 rounded p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-medium text-gray-500">Regra ${index + 1}</span>
          <button type="button" class="text-gray-400 hover:text-red-600 cursor-pointer"
                  data-action="configuration-conditions#removeCondition" title="Remover regra">\u00D7</button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Campo</label>
            <select name="${prefix}[field]"
                    class="select select-bordered w-full bg-white h-10 min-h-10"
                    data-action="change->configuration-conditions#updateField">
              ${this.buildFieldOptions()}
            </select>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Condição</label>
            <select name="${prefix}[operator]"
                    class="select select-bordered w-full bg-white h-10 min-h-10"
                    data-action="change->configuration-conditions#updateOperator">
              ${this.buildOperatorOptions()}
            </select>
          </div>

          <div data-value-container>
            <label class="block text-xs font-medium text-gray-700 mb-1">Valor</label>
            <input type="text" name="${prefix}[value]" placeholder="Digite o valor..."
                   class="input input-bordered w-full bg-white h-10 min-h-10" />
          </div>
        </div>
      </div>
    `
  }

  buildFieldOptions() {
    const groups = {}
    for (const [value, meta] of Object.entries(this.constructor.FIELD_METADATA)) {
      const group = meta.group || 'Outros'
      if (!groups[group]) groups[group] = []
      groups[group].push({ value, label: meta.label })
    }

    let html = '<option value="">Selecione um campo...</option>'
    for (const [group, options] of Object.entries(groups)) {
      html += `<optgroup label="${group}">`
      for (const opt of options) {
        html += `<option value="${opt.value}">${opt.label}</option>`
      }
      html += '</optgroup>'
    }
    return html
  }

  buildOperatorOptions(fieldType = 'text') {
    if (fieldType === 'offer_type') {
      return `
        <option value="">Selecione uma condição...</option>
        <optgroup label="Cliente ou não">
          <option value="blank">É cliente comum (buscando imóvel)</option>
          <option value="present">Não é cliente (parceria, prestador, vaga ou retirada de anúncio)</option>
        </optgroup>
        <optgroup label="Tipo específico">
          <option value="equals">= É exatamente</option>
          <option value="not_equals">≠ É qualquer um, exceto</option>
        </optgroup>
      `
    }

    if (fieldType === 'day_of_week') {
      return `
        <option value="in">∈ Está nos dias selecionados</option>
        <option value="not_in">∉ Não está nos dias selecionados</option>
      `
    }

    if (fieldType === 'boolean') {
      return `<option value="equals" selected>= É igual a</option>`
    }

    if (fieldType === 'select') {
      return `
        <option value="">Selecione uma condição...</option>
        <option value="equals">= É igual a</option>
        <option value="not_equals">≠ É diferente de</option>
      `
    }

    if (fieldType === 'number') {
      return `
        <option value="">Selecione uma condição...</option>
        <optgroup label="Comparação">
          <option value="equals">= É igual a</option>
          <option value="not_equals">≠ É diferente de</option>
        </optgroup>
        <optgroup label="Número">
          <option value="greater_than">> É maior que</option>
          <option value="greater_than_or_equal">≥ É maior ou igual a</option>
          <option value="less_than">< É menor que</option>
          <option value="less_than_or_equal">≤ É menor ou igual a</option>
        </optgroup>
        <optgroup label="Presença">
          <option value="present">✓ Tem valor</option>
          <option value="blank">∅ Está vazio</option>
        </optgroup>
      `
    }

    return `
      <option value="">Selecione uma condição...</option>
      <optgroup label="Comparação">
        <option value="equals">= É igual a</option>
        <option value="not_equals">≠ É diferente de</option>
      </optgroup>
      <optgroup label="Texto">
        <option value="contains">⊃ Contém</option>
        <option value="not_contains">⊅ Não contém</option>
        <option value="starts_with">⇥ Começa com</option>
        <option value="ends_with">⇤ Termina com</option>
      </optgroup>
      <optgroup label="Presença">
        <option value="present">✓ Tem valor</option>
        <option value="blank">∅ Está vazio</option>
      </optgroup>
      <optgroup label="Número">
        <option value="greater_than">> É maior que</option>
        <option value="greater_than_or_equal">≥ É maior ou igual a</option>
        <option value="less_than">< É menor que</option>
        <option value="less_than_or_equal">≤ É menor ou igual a</option>
      </optgroup>
    `
  }

  rebuildValueInput(container, field, currentValue, operator) {
    const meta = this.constructor.FIELD_METADATA[field] || { type: 'text' }
    const existingInput = container.querySelector('input, select')
    const inputName = existingInput?.name || this.inferInputName(container)

    if (operator === 'present' || operator === 'blank') {
      container.classList.add('hidden')
      container.innerHTML = `
        <label class="block text-xs font-medium text-gray-700 mb-1">Valor</label>
        <input type="hidden" name="${inputName}" value="" />
      `
      return
    }

    container.classList.remove('hidden')

    if (meta.type === 'day_of_week') {
      container.innerHTML = `
        <label class="block text-xs font-medium text-gray-700 mb-1">Dias</label>
        ${this.buildDayInput(inputName, currentValue)}
      `
      return
    }

    let inputHtml = ''

    switch (meta.type) {
      case 'offer_type':
        inputHtml = this.buildSelectInput(inputName, meta.options, currentValue)
        break
      case 'select':
        inputHtml = this.buildSelectInput(inputName, meta.options, currentValue)
        break
      case 'boolean':
        inputHtml = this.buildBooleanInput(inputName, currentValue)
        break
      case 'number':
        inputHtml = this.buildNumberInput(inputName, currentValue)
        break
      default:
        inputHtml = this.buildTextInput(inputName, currentValue)
    }

    container.innerHTML = `
      <label class="block text-xs font-medium text-gray-700 mb-1">Valor</label>
      ${inputHtml}
    `
  }

  buildSelectInput(name, options, currentValue) {
    const optionsHtml = options.map(opt =>
      `<option value="${opt.value}" ${opt.value === currentValue ? 'selected' : ''}>${opt.label}</option>`
    ).join('')

    return `
      <select name="${name}" class="select select-bordered w-full bg-white h-10 min-h-10">
        <option value="">Selecione...</option>
        ${optionsHtml}
      </select>
    `
  }

  buildBooleanInput(name, currentValue) {
    const trueChecked = currentValue === 'true' || currentValue === true || currentValue === null ? 'checked' : ''
    const falseChecked = currentValue === 'false' || currentValue === false ? 'checked' : ''

    return `
      <div class="flex items-center gap-6">
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="radio" name="${name}" value="true" class="radio" ${trueChecked} />
          <span class="text-sm text-gray-700">Sim</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="radio" name="${name}" value="false" class="radio" ${falseChecked} />
          <span class="text-sm text-gray-700">Não</span>
        </label>
      </div>
    `
  }

  buildNumberInput(name, currentValue) {
    return `
      <input type="number" name="${name}" value="${currentValue || ''}" placeholder="Digite um número..."
             class="input input-bordered w-full bg-white h-10 min-h-10" />
    `
  }

  buildTextInput(name, currentValue) {
    return `
      <input type="text" name="${name}" value="${currentValue || ''}" placeholder="Digite o valor..."
             class="input input-bordered w-full bg-white h-10 min-h-10" />
    `
  }

  buildDayInput(name, currentValue) {
    const selected = Array.isArray(currentValue) ? currentValue.map(Number) : []
    const checkboxes = this.constructor.DAY_OF_WEEK_OPTIONS.map(day => {
      const checked = selected.includes(day.value)
      return `
        <label class="${this.dayLabelClass(checked)}">
          <input type="checkbox" value="${day.value}" class="hidden" ${checked ? 'checked' : ''}
                 data-action="change->configuration-conditions#dayCheckboxChanged" />
          ${day.label}
        </label>
      `
    }).join('')

    return `
      <div class="flex flex-wrap gap-1.5">${checkboxes}</div>
      <input type="hidden" name="${name}" value="${JSON.stringify(selected)}" />
    `
  }

  dayLabelClass(checked) {
    return 'inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-all ' +
      (checked ? 'bg-yellow-50 border-yellow-400 text-yellow-800' : 'border-gray-200 text-gray-500 hover:border-gray-300')
  }

  buildEmptyState() {
    return `
      <div data-configuration-conditions-target="emptyState"
           class="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-300 rounded">
        Nenhuma regra adicionada ainda. Clique em "Adicionar Condição" para começar.
      </div>
    `
  }

  inferInputName(container) {
    const item = container.closest('.condition-item')
    if (!item) return 'conditions[0][value]'

    const fieldSelect = item.querySelector('select[name$="[field]"]')
    if (fieldSelect) {
      return fieldSelect.name.replace('[field]', '[value]')
    }

    return 'conditions[0][value]'
  }
}
